import { zodResolver } from '@hookform/resolvers/zod';
import type { SignInPage } from '@server/shared-sign-in';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';

import { emailSchema, type EmailValues } from '../../lib/authFormSchema';
import { type HostedUiConfig, startHostedUiSignIn } from '../../lib/hosted-ui';
import { cognitoStartUrl } from '../../lib/lookout-session';
import { findSsoIdentifier } from '../../lib/rudderstack-auth-client';
import { StepHeading } from './StepHeading';
import { TextButton } from './TextLink';

/**
 * Finds the work email's SSO identity provider, then opens its sign-in: in the page with the shared session (ACT2-879),
 * otherwise through Lookout's server flow.
 */
export function SsoSignInForm({
  apiBaseUrl,
  hostedUi,
  returnTo,
  invite,
  fromWebApp = false,
  page = 'login',
  onBack,
}: {
  apiBaseUrl: string;
  hostedUi: HostedUiConfig | null;
  returnTo: string | null;
  invite: boolean;
  fromWebApp?: boolean;
  /** The page that shows this form. An SSO error returns to it. */
  page?: SignInPage;
  onBack: () => void;
}): React.ReactElement {
  const form = useForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onTouched',
    defaultValues: { email: '' },
  });
  const lookup = useMutation({
    mutationFn: async (email: string) => {
      const identifier = await findSsoIdentifier(apiBaseUrl, email);
      if (identifier) {
        const target = { provider: 'sso', idp: identifier } as const;
        if (hostedUi) await startHostedUiSignIn(hostedUi, target, { returnTo, invite, fromWebApp, page });
        else window.location.assign(cognitoStartUrl(target, { returnTo, invite, fromWebApp, page }));
      }
      return identifier;
    },
  });
  const leaving = lookup.isPending || (lookup.isSuccess && lookup.data !== null);

  return (
    <div className="flex flex-col">
      <StepHeading title="Sign in with SSO" description="Enter your work email to continue with your organization." />
      <div className="mt-6 flex flex-col gap-4 text-left">
        {lookup.error && (
          <Callout variant="error" role="alert">
            {lookup.error.message}
          </Callout>
        )}
        {lookup.isSuccess && lookup.data === null && (
          <Callout variant="error" role="alert">
            SSO is not set up for this email.
          </Callout>
        )}
        <Form {...form}>
          <form
            noValidate
            aria-label="Sign in with SSO"
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit((values) => lookup.mutate(values.email))}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Work email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" size="lg" autoFocus autoComplete="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={leaving}>
              Continue
            </Button>
          </form>
        </Form>
      </div>
      <div className="mt-4 flex justify-center">
        <TextButton disabled={leaving} onClick={onBack}>
          Back to sign in
        </TextButton>
      </div>
    </div>
  );
}
